import { GridViewMetadata } from 'apps/webcomponents/src/grid/gridView/types';
import { ColumnDetails, ColumnType } from 'apps/webcomponents/src/grid/types/common';
import { SelectEvents, SelectOption } from 'apps/webcomponents/src/ui/gzSelect/types';
import {
  ComputersAndVirtualMachinesGridDataProvider
} from '../dataProvider/ComputersAndVirtualMachinesGridDataProvider';
import {
  Column,
  ColumnKeys,
  ComputersAndVirtualMachinesWebComponentSchema, ComputersAndVmItem, ComputersAndVmRowDataGrid,
  Dependencies, DomSchema,
  getTranslatedQuarantineActionStatusFilterValues,
  getTranslatedQuarantineActionStatusToName,
  Permissions, QuarantineActionStatus, RetrievedQuarantineFileData,
  getTranslatedQuarantineModuleFilterValues,
  SelectableAction,
} from '../types';

import moment from 'moment';
import { Filter } from 'apps/webcomponents/src/grid/gridFilters/types';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { Feature, RELATIVE_TIME_RANGES } from '@nirvana/shared/constants';
import { html } from 'lit';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { translateFn } from '../../../utils/translator.helper';
import { ComputersAndVirtualMachinesGridEvents } from '../events/events';
import { QuarantineActionEnabler } from '../helpers/quarantineActionsEnabler';
import { fileSizeConvertor } from "../../../utils/files.utils";
import { renderDetectingTechnology } from './customGridRenderers';

export class GridConfig {

  private dom: DomSchema;

  private columnRenderers = {
    [ColumnKeys.ENDPOINT_NAME]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.source?.protectedEntity?.name}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.IP]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.source?.protectedEntity?.ip}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.COMPANY]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.source?.company?.name}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.FILE]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.source?.filePath}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.MODULE]: renderDetectingTechnology.bind(this),
    [ColumnKeys.THREAT_NAME]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.threats?.name}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.QUARANTINED]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${moment(rowData?.item?.quarantinedDate).format('DD MMM YYYY, HH:mm')}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.ACTION_STATUS]: rowData => {
      const QuarantineActionStatusToName = getTranslatedQuarantineActionStatusToName();

      return html`<gz-grid-cell-default-renderer
        .label="${QuarantineActionStatusToName[rowData?.item?.actionStatus]}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.FILE_SIZE]: rowData => {
      let fileSize: string = 'N/A';
      if (rowData?.item?.size) {
        fileSize = fileSizeConvertor(rowData?.item?.size);
      }

      return html`
        <gz-grid-cell-default-renderer
          .label="${fileSize}"
        ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.SELECT]: this.actionButtonsRenderer.bind(this)
  };

  private pageSizeOptions = [
    {
      title: '20',
      value: '20',
    },
    {
      title: '50',
      value: '50',
    },
    {
      title: '100',
      value: '100',
    },
  ];

  private filtersUiConfig = [
    {
      key: ColumnKeys.QUARANTINED,
      config: {
        activeRange: null,
      },
    },
  ];

  public getDataForView(
    dependencies: Dependencies,
    dom: DomSchema,
    externalFilters: Filter<ColumnKeys>[]
  ): ComputersAndVirtualMachinesWebComponentSchema {
    const { permissions, translateFn } = dependencies;

    this.dom = dom;

    let companyFilter = dependencies.getCurrentUserCompany().id;

    if (Application.Main.getUser()['mdrSuperAnalyst']) {
      companyFilter = dependencies.getActiveCompany().companyId
    }

    const viewMetaData: GridViewMetadata<Column, ColumnKeys> = {
      viewId: 'quarantine-computers-and-virtual-machines',
      filters: this.getFilters(permissions, companyFilter, externalFilters),
      predefinedFiltersKeys: this.getPredefinedFiltersKeys(permissions),
      columns: this.getColumns(),
      pageSize: 100,
      isCompactViewChecked: false,
      hasSelectableRows: true,
      hasPagination: true,
      pageSizeOptions: this.pageSizeOptions,
      columnRenderers: this.columnRenderers,
      showGlobalActions: true,
      columnConfigs: [],
      allColumnDetails: this.getColumnDetails(translateFn, permissions, companyFilter),
      pageNumber: 1,
      filtersUiConfigs: this.filtersUiConfig,
      dataProvider: new ComputersAndVirtualMachinesGridDataProvider(dependencies),
      enableCSVExport: false,
      sortColumnName: ColumnKeys.QUARANTINED,
      sortDirection: 'desc',
      cellClassNameGenerator: this.getCellClassNameGenerator,
    };

    return {
      viewMetaData,
      actions: this.getActions(dependencies.translateFn),
      canHaveCompanyDownload: permissions.canHaveCompanyDownload
    };
  }

  private getFilters(
    permissions: Permissions,
    companyId: string,
    externalFilters: Filter<ColumnKeys>[]
  ): Filter<ColumnKeys>[] {
    let filters: Filter<ColumnKeys>[] = [
      {
        [ColumnKeys.QUARANTINED]: {
          timeRange: {
            kind: TimeRangeKind.RELATIVE_TIME_RANGE,
            relative: RELATIVE_TIME_RANGES.LAST_30_DAYS,
          },
        },
      },
      {
        [ColumnKeys.ACTION_STATUS]: {
          value: '',
        },
      },
    ];

    if (permissions.canHaveChildCompanies) {
      filters = [
        {
          [ColumnKeys.COMPANY]: {
            in: [companyId],
          },
        },
        ...filters,
      ];
    }

    for (const externalFilter of externalFilters) {
      const field = Object.keys(externalFilter)[0],
        filterIndex = filters.findIndex((filter: Filter<ColumnKeys>): boolean => Object.keys(filter).includes(field));

      if (-1 === filterIndex) {
        filters.push(externalFilter);
      } else {
        filters[filterIndex] = externalFilter;
      }
    }

    return filters;
  }

  private getPredefinedFiltersKeys(permissions: Permissions): Array<ColumnKeys> {
    let keys = [ColumnKeys.QUARANTINED, ColumnKeys.ACTION_STATUS];

    if (permissions.canHaveChildCompanies) {
      keys = [ColumnKeys.COMPANY, ...keys];
    }

    return keys;
  }

  private getColumns(): Column[] {
    return [
      Column.ENDPOINT_NAME,
      Column.IP,
      Column.COMPANY,
      Column.FILE,
      Column.THREAT_NAME,
      Column.QUARANTINED,
      Column.ACTION_STATUS,
      Column.SELECT,
    ];
  }

  private getColumnDetails(
    translateFn: Function,
    permissions: Permissions,
    companyId: string,

  ): Array<ColumnDetails<Column, ColumnKeys>> {
    const columnDetails = [
      {
        column: Column.ENDPOINT_NAME,
        key: ColumnKeys.ENDPOINT_NAME,
        columnName: translateFn('Endpoint name'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        column: Column.IP,
        key: ColumnKeys.IP,
        columnName: 'IP',
        type: ColumnType.STRING,
        removable: true,
        sortable: false,
      },
      {
        column: Column.FILE,
        key: ColumnKeys.FILE,
        columnName: translateFn('File'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        column: Column.FILE_SIZE,
        key: ColumnKeys.FILE_SIZE,
        columnName: translateFn('File size'),
        type: ColumnType.NUMBER,
        removable: true,
        sortable: true,
        filteringDisabled: true,
      },
      {
        column: Column.MODULE,
        key: ColumnKeys.MODULE,
        columnName: translateFn('Detecting Technology'),
        type: ColumnType.SELECT,
        values: getTranslatedQuarantineModuleFilterValues(),
        removable: true,
        sortable: true,
      },
      {
        column: Column.THREAT_NAME,
        key: ColumnKeys.THREAT_NAME,
        columnName: translateFn('Threat name'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
      },
      {
        column: Column.QUARANTINED,
        key: ColumnKeys.QUARANTINED,
        columnName: translateFn('Quarantined on'),
        type: ColumnType.DATE,
        removable: true,
        sortable: true,
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.RELATIVE_TIME_RANGE,
            relative: RELATIVE_TIME_RANGES.LAST_30_DAYS,
          },
        },
      },
      {
        column: Column.ACTION_STATUS,
        key: ColumnKeys.ACTION_STATUS,
        columnName: translateFn('Action status'),
        type: ColumnType.SELECT,
        values: getTranslatedQuarantineActionStatusFilterValues(),
        removable: true,
        sortable: true,
      },
    ];

    if (permissions.canHaveChildCompanies) {
      const indexToInsert = 2;
      const deleteCount = 0;
      const columnToInsert = {
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.COMPANY,
        removable: false,
        sortable: true,
        filterResetValue: [companyId],
      };

      // @ts-ignore
      columnDetails.splice(indexToInsert, deleteCount, columnToInsert);
    }

    if (Application.Main.product.isCloud()) {
      columnDetails.push({
        column: Column.SELECT,
        key: ColumnKeys.SELECT,
        columnName: '',
        type: ColumnType.ACTION,
        removable: false,
        sortable: false,
      })
    }

    return columnDetails;
  }

  private getActions(translateFn: any): Array<SelectOption> {
    return [
      {
        title: translateFn('Release as attachment'),
        value: SelectableAction.RESTORE,
        disabled: true,
        id: 'restore',
      },
      {
        title: translateFn('Release to intended recipient'),
        value: SelectableAction.RESTORE_ORIGINAL,
        disabled: true,
        id: 'restoreOriginal',
      },
    ];
  }

  private getCellClassNameGenerator(_, rowData): string {
    const {
      item: { isGhost },
    } = rowData;

    return isGhost ? 'cell-ghost' : '';
  }

  /**
   * Render row action buttons
   *
   * @param rowData
   * @private
   */
  private actionButtonsRenderer(rowData: ComputersAndVmRowDataGrid): HTMLElement | void {
    const quarantine: ComputersAndVmItem = rowData.item;

    if (!Application.Main.product.isCloud() || !QuarantineActionEnabler.userHasDownloadQuarantineRights()) {
      return;
    }

    if (!quarantine.archiveSpecifics) {
      return;
    }

    const fileId:string = quarantine.archiveSpecifics.fileId;

    if (!fileId || quarantine.actionStatus !== QuarantineActionStatus.ACTION_STATUS_SUCCEEDED_RETRIEVE) {
      return;
    }

    const options: Array<SelectOption> = [];
    const submittedToLabs = (quarantine.archiveSpecifics?.submittedToLabs ?? 0) !== 0;

    if (quarantine.canSubmitToSandbox && Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_SANDBOX_ANALYZER_USABLE)) {
      options.push(
        {
          title: translateFn('Submit to Sandbox Analyzer'),
          value: ComputersAndVirtualMachinesGridEvents.OnSubmitToSandboxQuarantineFile,
          id: quarantine._id,
        }
      );
    }

    options.push(
      {
        title: translateFn('Submit to Bitdefender Labs'),
        value: ComputersAndVirtualMachinesGridEvents.OnSubmitToLabsQuarantineFile,
        id: quarantine._id,
        disabled: submittedToLabs,
        tooltipText: submittedToLabs ? translateFn('The file was already submitted to Bitdefender Labs.') : null,
      },
      {
        title: translateFn('Download retrieved file'),
        value: ComputersAndVirtualMachinesGridEvents.OnDownloadRetrievedQuarantineFile,
        id: quarantine._id,
      },
      {
        title: translateFn('Delete retrieved file'),
        value: ComputersAndVirtualMachinesGridEvents.OnDeletedRetrievedQuarantineFile,
        id: quarantine._id,
        customClass: 'danger'
      },
    );
    const gzSelect: GzSelectWebComponent = new GzSelectWebComponent();
    const cellContainer: HTMLElement = document.createElement('div');
    cellContainer.className = 'd-flex justify-end w-full';
    gzSelect.options = options;
    gzSelect.innerHTML = `<gz-icon-button
          icon='ds-icon-more'
          fontSize='24'
          buttonType='square'
          buttonFill='quiet'
          buttonColor='secondary'>
     </gz-icon-button>`;

    gzSelect.addEventListener(SelectEvents.SELECTION_CHANGED, (e: CustomEvent) => {
      const selectedOption: ComputersAndVirtualMachinesGridEvents = e.detail.selected;
      const quarantineData: RetrievedQuarantineFileData = {
        quarantineId: quarantine._id,
        fileId: fileId,
        archiveName: quarantine.archiveSpecifics?.archiveName ?? '',
        companyId: quarantine.source.company.id,
        fileSize: quarantine.size,
      };

      const event = new CustomEvent(selectedOption, {
        detail: quarantineData,
        bubbles: true,
        composed: true,
      });

      this.dom.$grid.dispatchEvent(event);
    });

    cellContainer.appendChild(gzSelect);

    return cellContainer;
  }
}
